import {
  Box,
  Typography,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { ComponentProps } from "./types";
import { memo, useEffect, useState } from "react";
import moment from "moment";
import { getSubstanceFrequencyDataThunk } from "../../store/thunk/mainDashboardThunk";
import { useAppDispatch, useAppSelector } from "../../store/store";
import BarChartLoader from "./BarChartLoader";
import { useNavigate } from "react-router-dom";
// import HighchartsReact from 'highcharts-react-official';
// import Highcharts from 'highcharts';
// import "highcharts/highcharts-more";

// Apply `highcharts-more` to extend Highcharts

// ChartJS.register(ArcElement, Tooltip, ChartDataLabels);

const SubstanceRiskLongevity = ({
  id,
  index,
  pin,
  pinComponentHandler,
  selectedValueFacility,
  facilityIds,
  locationIds,
  currentTenant,
  ...rest
}: ComponentProps) => {
  const navigate = useNavigate();
  const [chartOptions, setChartOptions] = useState<{
    series: any[];
    options: ApexCharts.ApexOptions;
  }>({
    series: [],
    options: {
      chart: {
        type: "bar",
        height: 350,
        stacked: true,
        toolbar: {
          show: true,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
      plotOptions: {
        bar: {
          horizontal: false,
          // borderRadius: 10,
          // borderRadiusApplication: 'end', // 'around', 'end'
          // borderRadiusWhenStacked: 'all', // 'all', 'last'
          columnWidth: "80%",
          distributed: false,
          barHeight: "30px",
          // dataLabels: {
          //     total: {
          //         enabled: true,
          //         formatter: (val: any) => {
          //             return `${formatDuration(val as number)}`;
          //         },
          //         style: {
          //             fontSize: '13px',
          //             fontWeight: 900
          //         }
          //     }
          // }
        },
      },
      dataLabels: {
        formatter(val: number, opts) {
          return `${formatDuration(val)}`;
        },
        style: {
          colors: ["#000", "#fff", "#fff"],
          fontSize: "10px",
        },
      },
      grid: {
        show: false,
      },
      xaxis: {
        type: "category",
        categories: [],
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
      legend: {
        position: "bottom",
        offsetY: 40,
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        y: {
          formatter: (val) => `${formatDuration(val)}`,
        },
        marker: {
          show: true,
        },
      },
      noData: {
        text: "No Data Available",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: "#666", // Text color
          fontSize: "14px", // Font size
        },
      },
    },
  });
  const { userData } = useAppSelector((store) => store?.authReducer);
  const substanceData = useAppSelector(
    (store) => store?.substanceSlice?.substanceConfigurationList
  );
  const dispatch = useAppDispatch();
  const [filterType, setFilterType] = useState("day");
  const substanceFrequency = useAppSelector(
    (store) => store?.mainDashboard?.substanceFrequency
  );
  const locationLoader = useAppSelector(
    (store) => store?.locationSlice?.loading
  );
  const tenantLoader = useAppSelector((store) => store?.tenantSlice?.loading);
  const [loader, setLoader] = useState(true);
  useEffect(() => {
    setLoader(substanceFrequency?.loading || locationLoader || tenantLoader);
  }, [substanceFrequency?.loading, locationLoader, tenantLoader]);
  const [filterDate, setFilterDate] = useState({
    startDate: moment().startOf("day").utc().valueOf(),
    endDate: moment().utc().valueOf(),
    // endDate: moment().endOf('day').utc().valueOf(),
  });
  const filterHandler = (value: moment.unitOfTime.StartOf) => {
    const startDate = moment().startOf(value).utc().valueOf();
    const endDate = moment().utc().valueOf();
    // const endDate = moment().endOf(value).utc().valueOf();
    setFilterType(value as string);
    setFilterDate({
      startDate,
      endDate,
    });
  };

  useEffect(() => {
    const facilityKey = facilityIds?.replace("all,", "");
    if (
      /* currentTenant?.tenantCode && */ facilityKey ||
      locationIds /* || userData?.group === 'admin' */
    ) {
      dispatch(
        getSubstanceFrequencyDataThunk({
          tenantCode: currentTenant?.tenantCode as string,
          facilityKey: facilityKey,
          locationId: locationIds,
          ...filterDate,
        })
      );
    }
  }, [
    /* currentTenant?.tenantCode, */ facilityIds,
    locationIds,
    filterDate /* userData?.group */,
  ]);

  useEffect(() => {
    if (currentTenant?.tenantCode) {
      filterGraphData();
    }
  }, [JSON.stringify(substanceFrequency?.data), JSON.stringify(substanceData)]);

  const handleClick = (item: { substanceKey: string }) => {
    const queryParams = new URLSearchParams({
      range: filterType,
    });
  
    if (selectedValueFacility) {
      queryParams.append("facility", selectedValueFacility);
    }
  
    navigate(`/element-detection/${item.substanceKey}?${queryParams.toString()}`);
  };

  const filterGraphData = () => {
    let data: {
      [category: string]: {
        [key: string]: number;
      };
    } = {};
    substanceFrequency?.data
      ?.filter((value) => value?.index !== 1)
      .map((value) => {
        const substanceDetails = substanceData?.find((sub: any) =>
          value?.pk?.includes(sub?.sk)
        );
        const category = (
          substanceDetails?.displayName
            ? substanceDetails?.displayName
            : value?.pk?.replaceAll("C|", "")
        )?.toUpperCase();
        const colorIndex = value?.index === 3 ? 2 : value?.index;
        data = {
          ...data,
          [category]: {
            ...data?.[category],
            [colorIndex]:
              (data?.[category]?.[colorIndex] || 0) +
              value?.total_diff_duration,
          },
        };
      });
    const categoryData = Object.keys(data);
    const names = [2, 4, 5];
    const formatData = names
      .map((name) => ({
        name: name == 2 ? "Yellow" : name == 4 ? "Red" : "Black",
        color: name == 2 ? "#ffef5c" : name == 4 ? "#871014" : "#000000",
        data: categoryData.map((cat) => data[cat][name] ?? 0),
      }))
      .filter(
        (value) => !!value?.data?.reduce((a: number, b: number) => a + b, 0)
      );

    setChartOptions((prev) => {
      return {
        series: formatData,
        options: {
          ...prev.options,
          xaxis: {
            categories: categoryData?.length > 0 ? categoryData : [""],
          },
          chart: {
            ...prev.options.chart,
            events: {
              dataPointSelection: (event, chartContext, config) => {

                const selectedPk =
                  categoryData[config.dataPointIndex].toLowerCase();

                const substanceKey = substanceData.find((item: any) =>
                  item.displayName.toLowerCase() === selectedPk.toLowerCase()
                )?.substanceKey;

                if (substanceKey) {
                  handleClick({ substanceKey });
                }
              },
            },
          },
          plotOptions: {
            ...prev?.options?.plotOptions,
            bar: {
              ...prev?.options?.plotOptions?.bar,
              columnWidth: categoryData?.length <= 2 ? "30%" : "80%",
            },
          },
        },
      };
    });
  };

  return (
    <Card
      sx={{
        borderRadius: "3px !important",
        border: "none",
        backgroundColor: "#fff !important",
        boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.1) !important",
        width: "100% !important",
        paddingLeft: "0px !important",
        paddingRight: "0px !important",
      }}
    >
      <Box
        onClick={() => pinComponentHandler(id)}
        sx={{
          position: "absolute",
          display: "flex",
          justifyContent: "flex-end",
          top: 14,
          right: 14,
          cursor: "pointer",
          zIndex: 9999,
        }}
      >
        {pin ? (
          <img src="./assets/images/pin-filled.png" alt="pin" width={15} />
        ) : (
          <img src="./assets/images/pin-outline.png" alt="pin" width={15} />
        )}
      </Box>
      <CardContent
        sx={{
          height: 450,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            mr: 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box
            className="title"
            {...(pin ? {} : rest)}
            sx={{
              paddingX: "1em",
              ":hover": pin
                ? {}
                : {
                    backgroundColor: "#f4f4f4",
                    borderRadius: "10px",
                    cursor: "grab",
                  },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bolder" }}>
              Characteristics Threshold Longevity
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            sx={{ display: "flex", gap: 2 }}
          >
            <ToggleButton
              disabled={loader || filterType === "day"}
              value="day"
              className={`filter-button ${
                filterType === "day" && "filter-button-color-black"
              }`}
              onClick={(e, value) => {
                filterHandler(value);
              }}
            >
              Daily
            </ToggleButton>
            <ToggleButton
              disabled={loader || filterType === "week"}
              value="week"
              className={`filter-button ${
                filterType === "week" && "filter-button-color-black"
              }`}
              onClick={(e, value) => {
                filterHandler(value);
              }}
            >
              Weekly
            </ToggleButton>
            <ToggleButton
              disabled={loader || filterType === "month"}
              value="month"
              className={`filter-button ${
                filterType === "month" && "filter-button-color-black"
              }`}
              onClick={(e, value) => {
                filterHandler(value);
              }}
            >
              Monthly
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ height: "300px", display: "flex" }}>
          <Box sx={{ flexGrow: 1, maxWidth: loader ? "90%" : "auto" }}>
            {!loader ? (
              <ReactApexChart
                options={chartOptions?.options}
                series={chartOptions.series}
                type="bar"
                height={350}
              />
            ) : (
              <Box
                key={index}
                sx={{
                  paddingX: 3,
                  alignContent: "end",
                  height: "100% !important",
                  display: "flex",
                  alignItems: "end",
                  gap: "1px",
                  justifyContent: "center",
                }}
              >
                <BarChartLoader />
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
export default memo(SubstanceRiskLongevity);
function formatDuration(seconds: number): string {
  const days = Math.floor(seconds / (24 * 60 * 60));
  const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((seconds % (60 * 60)) / 60);
  const remainingSeconds = seconds % 60;

  const parts: string[] = [];

  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (remainingSeconds > 0) parts.push(`${remainingSeconds}s`);

  return parts.join(" ");
}
